import React, { useEffect, useMemo } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { useGetCalendarMeetingQuery } from '../../api/calendar.js';
import AuthLayout from '../../components/layout/AuthLayout.jsx';
import AfterMeeting from '../../components/misc/AfterMeeting.jsx';
import BaseCard from '../../components/misc/BaseCard.jsx';
import BeforeMeeting from '../../components/misc/BeforeMeeting.jsx';
import DuringMeeting from '../../components/misc/DuringMeeting.jsx';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { getMeetingStatus } from '../../utils/meetingStatus.js';

const MeetingPreparation = () => {
    const navigate = useNavigate();

    const { id } = useParams();

    const { data: meetingResponse, isLoading } = useGetCalendarMeetingQuery(id);

    const calendarMeeting = useMemo(() => {
        if (meetingResponse?.response?.meeting) {
            return meetingResponse.response.meeting;
        }

        return null;
    }, [meetingResponse]);

    const { status, started, ended } = getMeetingStatus(calendarMeeting);

    const { handleSubmit, getValues, formState, control, clearErrors, reset } = useForm({
        defaultValues: {
            participants: []
        }
    });

    const { fields: fieldsParticipants } = useFieldArray({
        control,
        name: 'participants'
    });

    useEffect(() => {
        if (calendarMeeting) {
            const newParticipants = {
                participants: calendarMeeting.participants.map((participant) => ({
                    ...participant,
                    participant_id: participant.id
                }))
            };
            reset(newParticipants);
        }
    }, [calendarMeeting, reset]);

    if (isLoading) {
        return (
            <AuthLayout title='Calendar' backRoute='/calendar'>
                <div className='text-center mt-[100px]'>
                    <ThreeDotsLoader />
                    <div className='text-theme1-primary font-bold text-xl mt-[10px]'>Loading meeting room...</div>
                </div>
            </AuthLayout>
        );
    }

    if (!calendarMeeting) return null;

    return (
        <AuthLayout title='Calendar' backRoute='/calendar' mainClassNames={'flex flex-col'}>
            <div className='flex flex-col max-w-3xl w-full mx-auto px-8'>
                <div className={`flex  ${!started ? 'my-6' : ended ? 'my-6' : 'mt-6 mb-0'}`}>
                    <div className='text-theme1-title2 font-bold text-[22px]'>{status}</div>
                </div>
            </div>
            <BaseCard
                className={`border-0 p-0 m-0 shadow-none ${
                    !started
                        ? 'bg-theme1-tan mb-6'
                        : ended
                          ? 'bg-theme1-tan mb-6'
                          : 'flex flex-col flex-grow bg-transparent mb-0'
                }`}
            >
                {!started ? (
                    <BeforeMeeting
                        calendarMeeting={calendarMeeting}
                        fieldsParticipants={fieldsParticipants}
                        control={control}
                        clearErrors={clearErrors}
                    />
                ) : ended ? (
                    <AfterMeeting
                        meetingId={id}
                        calendarMeeting={calendarMeeting}
                        fieldsParticipants={fieldsParticipants}
                    />
                ) : (
                    <DuringMeeting id={id} started={started} ended={ended} />
                )}
            </BaseCard>
        </AuthLayout>
    );
};

export default MeetingPreparation;
